import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiLock, FiSave, FiCamera } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [tab, setTab] = useState('profile');
  const [saving, setSaving] = useState(false);

  const { register: regProfile, handleSubmit: handleProfile, formState: { errors: pe } } = useForm({
    defaultValues: { name: user?.name || '', email: user?.email || '', phone: user?.phone || '' }
  });
  const { register: regPass, handleSubmit: handlePass, reset: resetPass, formState: { errors: pwe }, watch } = useForm();

  const saveProfile = async (data) => {
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(data);
      updateUser(res.data.data || res.data);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const changePassword = async (data) => {
    setSaving(true);
    try {
      await authAPI.changePassword(data);
      toast.success('Password changed!');
      resetPass();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-salon-cream py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} className="mb-8">
          <h1 className="font-display text-3xl text-salon-dark">My Profile</h1>
          <p className="text-salon-muted">Manage your account information and security</p>
        </motion.div>

        {/* Profile Header Card */}
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
          className="card p-6 mb-6 flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-salon-primary/20 flex items-center justify-center text-3xl font-display text-salon-primary">
              {user?.name?.[0] || 'U'}
            </div>
            <button className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-salon-primary text-white flex items-center justify-center">
              <FiCamera className="text-xs" />
            </button>
          </div>
          <div>
            <h2 className="font-display text-xl text-salon-dark">{user?.name}</h2>
            <p className="text-salon-muted text-sm">{user?.email}</p>
            <span className="badge-primary text-xs mt-1 inline-block">Customer</span>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex border-b border-salon-cream-dark mb-6">
          {[{ id:'profile', label:'Personal Info' }, { id:'password', label:'Change Password' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors
                ${tab === t.id ? 'border-salon-primary text-salon-primary' : 'border-transparent text-salon-muted hover:text-salon-dark'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Profile Form */}
        {tab === 'profile' && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} className="card p-8">
            <form onSubmit={handleProfile(saveProfile)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-salon-dark mb-1">Full Name</label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-3.5 text-salon-muted" />
                  <input {...regProfile('name', { required:'Name is required' })}
                    className="input-field pl-10" placeholder="Your full name" />
                </div>
                {pe.name && <p className="text-red-500 text-xs mt-1">{pe.name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-salon-dark mb-1">Email Address</label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-3.5 text-salon-muted" />
                  <input {...regProfile('email', { required:'Email required', pattern: { value:/^\S+@\S+$/i, message:'Invalid email' } })}
                    type="email" className="input-field pl-10" placeholder="your@email.com" />
                </div>
                {pe.email && <p className="text-red-500 text-xs mt-1">{pe.email.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-salon-dark mb-1">Phone Number</label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-3.5 text-salon-muted" />
                  <input {...regProfile('phone')} className="input-field pl-10" placeholder="+91 00000 00000" />
                </div>
              </div>
              <button type="submit" disabled={saving}
                className="btn-primary flex items-center gap-2">
                <FiSave />{saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </motion.div>
        )}

        {/* Password Form */}
        {tab === 'password' && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} className="card p-8">
            <form onSubmit={handlePass(changePassword)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-salon-dark mb-1">Current Password</label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-3.5 text-salon-muted" />
                  <input {...regPass('current_password', { required:'Current password required' })}
                    type="password" className="input-field pl-10" placeholder="••••••••" />
                </div>
                {pwe.current_password && <p className="text-red-500 text-xs mt-1">{pwe.current_password.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-salon-dark mb-1">New Password</label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-3.5 text-salon-muted" />
                  <input {...regPass('password', { required:'New password required', minLength: { value:8, message:'Minimum 8 characters' } })}
                    type="password" className="input-field pl-10" placeholder="••••••••" />
                </div>
                {pwe.password && <p className="text-red-500 text-xs mt-1">{pwe.password.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-salon-dark mb-1">Confirm New Password</label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-3.5 text-salon-muted" />
                  <input {...regPass('password_confirmation', {
                    required:'Please confirm your password',
                    validate: v => v === watch('password') || 'Passwords do not match'
                  })} type="password" className="input-field pl-10" placeholder="••••••••" />
                </div>
                {pwe.password_confirmation && <p className="text-red-500 text-xs mt-1">{pwe.password_confirmation.message}</p>}
              </div>
              <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
                <FiLock />{saving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
